/**
 * A PERSON BEING ADDED, BEFORE THEY ARE A PERSON
 * ==============================================
 *
 * The draft the add-person sheet fills in, and the one question worth asking
 * about it: is there enough here to write somebody down?
 *
 * It lives away from the component because the answer is a rule about the
 * church rather than a detail of a form, and a rule with this many branches
 * ought to be testable without mounting React to ask it.
 */

export interface GuestDraft {
  key: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  /**
   * How the viewer knows them, as a Relationship_ID plus the gender the chip
   * implies — "Mom" answers both in one tap. Saved to MP whether or not this
   * registration is ever finished, because next year it is one fewer question
   * on every form, not just this one.
   */
  relationshipId: number | null;
  gender: 'male' | 'female' | null;
  /**
   * The chip they actually tapped, which is not recoverable from the two
   * fields above: a null `relationshipId` could mean either "skipped" or "not
   * asked yet", and without this the form opened with a chip already lit.
   */
  relationChoice: string | null;
  /**
   * Whether the relationship question has been PUT — answered or skipped.
   *
   * Separate from `relationChoice` for the same reason `whereAnswered` is
   * separate from `addToHousehold`: skipping is an answer, and it must not be
   * indistinguishable from a page nobody has reached yet.
   */
  relationAsked: boolean;
  /**
   * Whether this person joins the household or stays a related contact. Saying
   * no is not a dead end: the relationship is written either way, and the flag
   * on its type is what puts them in this list next time.
   */
  addToHousehold: boolean;
  /**
   * Whether the household question has been ANSWERED, which a boolean alone
   * cannot say: "no, they live elsewhere" is a real answer and must not be
   * indistinguishable from an untouched form.
   */
  whereAnswered: boolean;
  /** Only asked when they are joining the household, where MP needs it. */
  dateOfBirth: string;
  /**
   * A minor who lives elsewhere: their parent, so they get a real household
   * and an emergency contact instead of a record on their own. Undefined until
   * the question is reached; `unknown` is the answer "I don't know them".
   */
  parent?: ParentDraft;
  /**
   * Everything else a profile holds about a person, asked on one optional page
   * and only for somebody joining the household — because that is the case
   * where these are OUR records to fill in rather than a claim about
   * somebody else's. All of it can be left blank; none of it gates the button.
   */
  nickname: string;
  middleName: string;
  positionId: number | null;
  maritalStatusId: number | null;
  shirtSizeId: number | null;
  workPhone: string;
  /**
   * What MP made of them, once the sheet has saved.
   *
   * `added` means they exist and can be ticked; `pending` means MP already
   * held them and their family has to agree before this viewer may register
   * them. Undefined is a draft that has not been saved yet.
   */
  saved?: 'added' | 'pending';
  /** Their MP contact id, once they have one. */
  contactId?: number;
}

export interface ParentDraft {
  role: 'mom' | 'dad' | 'guardian' | 'unknown' | null;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

export const blankParent = (): ParentDraft => ({
  role: null,
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
});

const EMAIL = /.+@.+\..+/;

/** Under 18 on the birthday given. No birthday: not known to be a minor. */
export function isMinorOn(dateOfBirth: string, today: Date = new Date()): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOfBirth);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  let age = today.getFullYear() - y;
  if (today.getMonth() + 1 < mo || (today.getMonth() + 1 === mo && today.getDate() < d)) age -= 1;
  return age < 18;
}

/** Somebody living elsewhere who is a minor, so the parent question is asked. */
export const needsParent = (g: Pick<GuestDraft, 'addToHousehold' | 'dateOfBirth'>) =>
  !g.addToHousehold && isMinorOn(g.dateOfBirth);

/** A parent answer the server will take: "I don't know", or a name, a mobile and who they are. */
export function parentReady(p: ParentDraft | undefined): boolean {
  if (!p?.role) return false;
  if (p.role === 'unknown') return true;
  if (p.email.trim() && !EMAIL.test(p.email.trim())) return false;
  return Boolean(
    p.firstName.trim() && p.lastName.trim() && p.phone.replace(/\D/g, '').length >= 10
  );
}

/**
 * A guest counts once we know enough to write them down.
 *
 * Which is less than it looks. MP requires almost nothing of a Contact —
 * `Display_Name`, `Company`, `Domain_ID` and a row of bits that all default;
 * even the names and the email address are nullable — so the bar here is what
 * the CHURCH needs rather than what the column definition allows.
 *
 * A name, because a person without one is a row nobody can act on. The
 * relationship, because it is the fact worth more than this registration: it
 * lands on the record, and every one of them makes next year's list better.
 * And a birthday for anybody joining the household, because that is what
 * places them — check-in and age-gated registrations both fall over without
 * it, and it is the one thing a parent can answer instantly.
 *
 * Email splits on the branch, and for a reason that is not about MP at all.
 * Somebody joining the household does not need one — a nine-year-old has not
 * got one, and a form that insists is how a church ends up with a parent's
 * address on four different records. Somebody who is NOT joining does, because
 * it is the only thing that makes them findable: it is what a match is drawn
 * against, and it is where the confirmation has to be sent. A relationship
 * claimed about a person we cannot reach is a claim nobody can ever answer.
 */
export function guestReady(g: GuestDraft): boolean {
  const named = Boolean(g.firstName.trim() && g.lastName.trim());
  // The relationship is asked for and not required. It used to be required,
  // which was honoured by a "Someone else" chip that satisfied the check and
  // wrote no relationship — so the rule was already optional, it just made
  // people click something first. `relationAsked` is the real condition: the
  // question was PUT, and either answered or skipped.
  if (!named || !g.relationAsked || !g.whereAnswered) return false;
  // Typed, but typed wrong, is not ready — blank is, on the branch that
  // allows blank.
  if (g.email.trim() && !EMAIL.test(g.email.trim())) return false;
  // Living elsewhere: an email, or a birthday for the student who has none.
  // A minor also needs the parent question answered.
  if (g.addToHousehold) return Boolean(g.dateOfBirth);
  if (!(EMAIL.test(g.email.trim()) || g.dateOfBirth)) return false;
  return needsParent(g) ? parentReady(g.parent) : true;
}
